import React from 'react';
import { Box, Container, Typography, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import faqsContent from '../content/faqs.json';
import SectionCard from './SectionCard';

const Faqs = () => {
    const { title, intro, items } = faqsContent;

    const nestedPanelSx = {
        p: 2.5,
        borderRadius: 3,
        backgroundColor: '#f7deed',
        minWidth: 0,
        boxSizing: 'border-box',
        width: '100%'
    };

    const parseDetails = (details) => {
        const lines = details.split('\n');
        const blocks = [];
        let i = 0;

        const isBulletLine = (line) => /^-\s+/.test(line.trim());
        const isNumberedLine = (line) => /^\d+\.\s+/.test(line.trim());

        while (i < lines.length) {
            const current = lines[i].trim();

            if (!current) {
                i += 1;
                continue;
            }

            if (isBulletLine(current)) {
                const items = [];

                while (i < lines.length && isBulletLine(lines[i])) {
                    items.push(lines[i].trim().replace(/^-\s+/, ''));
                    i += 1;
                }

                blocks.push({ type: 'bulleted', items });
                continue;
            }

            if (isNumberedLine(current)) {
                const items = [];

                while (i < lines.length && isNumberedLine(lines[i])) {
                    items.push(lines[i].trim().replace(/^\d+\.\s+/, ''));
                    i += 1;
                }

                blocks.push({ type: 'numbered', items });
                continue;
            }

            const paragraphLines = [];
            while (
                i < lines.length &&
                lines[i].trim() &&
                !isBulletLine(lines[i]) &&
                !isNumberedLine(lines[i])
            ) {
                paragraphLines.push(lines[i].trim());
                i += 1;
            }

            blocks.push({
                type: 'paragraph',
                text: paragraphLines.join(' ')
            });
        }

        return blocks;
    };

    const renderInlineFormattedText = (text) => {
        const tokenRegex = /(\[.*?\]\(.*?\)|\*\*.*?\*\*)/g;
        const parts = text.split(tokenRegex).filter(Boolean);

        return parts.map((part, index) => {
            const linkMatch = part.match(/^\[(.*?)\]\((.*?)\)$/);
            if (linkMatch) {
                const [, label, href] = linkMatch;

                return (
                    <MuiLink
                        key={index}
                        component={RouterLink}
                        to={href}
                        sx={{
                            color: '#00476e',
                            fontWeight: 700,
                            textDecoration: 'underline',
                            textDecorationColor: 'rgba(0, 71, 110, 0.35)',
                            textUnderlineOffset: '3px',
                            '&:hover': {
                                color: '#ff751f',
                                textDecorationColor: '#ff751f'
                            }
                        }}
                    >
                        {renderInlineFormattedText(label)}
                    </MuiLink>
                );
            }

            const boldMatch = part.match(/^\*\*(.*?)\*\*$/);
            if (boldMatch) {
                return (
                    <Box key={index} component="span" sx={{ fontWeight: 700 }}>
                        {boldMatch[1]}
                    </Box>
                );
            }

            return <React.Fragment key={index}>{part}</React.Fragment>;
        });
    };

    const renderFaqContent = (item) => {
        const blocks = parseDetails(item.details);

        return (
            <>
                <Typography
                    className="heading-font"
                    variant="h5"
                    sx={{ color: '#00476e', fontWeight: 700, mb: 2 }}
                >
                    {item.title}
                </Typography>

                {blocks.map((block, index) => {
                    if (block.type === 'paragraph') {
                        return (
                            <Typography
                                key={index}
                                variant="body1"
                                sx={{
                                    color: 'rgba(0, 71, 110, 0.78)',
                                    lineHeight: 1.8,
                                    mb: index === blocks.length - 1 ? 0 : 2
                                }}
                            >
                                {renderInlineFormattedText(block.text)}
                            </Typography>
                        );
                    }

                    if (block.type === 'bulleted') {
                        return (
                            <Box
                                key={index}
                                component="ul"
                                sx={{
                                    pl: 3,
                                    my: 0,
                                    mb: index === blocks.length - 1 ? 0 : 2
                                }}
                            >
                                {block.items.map((listItem, itemIndex) => (
                                    <Box component="li" key={itemIndex} sx={{ mb: 0.75 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: 'rgba(0, 71, 110, 0.78)',
                                                lineHeight: 1.8
                                            }}
                                        >
                                            {renderInlineFormattedText(listItem)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        );
                    }

                    return (
                        <Box
                            key={index}
                            component="ol"
                            sx={{
                                pl: 3,
                                my: 0,
                                mb: index === blocks.length - 1 ? 0 : 2
                            }}
                        >
                            {block.items.map((listItem, itemIndex) => (
                                <Box component="li" key={itemIndex} sx={{ mb: 0.75 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: 'rgba(0, 71, 110, 0.78)',
                                            lineHeight: 1.8
                                        }}
                                    >
                                        {renderInlineFormattedText(listItem)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    );
                })}
            </>
        );
    };

    return (
        <Container
            maxWidth={false}
            sx={{
                py: { xs: 4, md: 7 },
                width: 'min(92vw, 1600px)',
                mx: 'auto'
            }}
        >
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <SectionCard>
                    <Typography
                        className="heading-font"
                        variant="h2"
                        sx={{
                            color: '#00476e',
                            fontWeight: 700,
                            mb: 2,
                            fontSize: { md: '2.7rem' }
                        }}
                    >
                        {title}
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: 'rgba(0, 71, 110, 0.82)',
                            lineHeight: 1.8,
                            maxWidth: 1100,
                            mb: 4
                        }}
                    >
                        {intro}
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3
                        }}
                    >
                        {items.map((item) => (
                            <Box key={item.title} sx={nestedPanelSx}>
                                {renderFaqContent(item)}
                            </Box>
                        ))}
                    </Box>
                </SectionCard>
            </Box>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <SectionCard sx={{ mb: 4 }}>
                    <Typography
                        className="heading-font"
                        variant="h2"
                        sx={{
                            color: '#00476e',
                            fontWeight: 700,
                            mb: 2,
                            fontSize: '2rem'
                        }}
                    >
                        {title}
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: 'rgba(0, 71, 110, 0.82)',
                            lineHeight: 1.8
                        }}
                    >
                        {intro}
                    </Typography>
                </SectionCard>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3
                    }}
                >
                    {items.map((item) => (
                        <SectionCard key={item.title}>
                            {renderFaqContent(item)}
                        </SectionCard>
                    ))}
                </Box>
            </Box>
        </Container>
    );
};

export default Faqs;